/* eslint @typescript-eslint/explicit-module-boundary-types: off */
import axiosInstance, { axiosMultipart } from './axios';
import { parseCommerces } from './commerceServices';
import App from 'types/models/App';
import PaginationData from 'types/models/PaginationData';
import Venue from 'types/models/Venue';
import VenuePaymentConfig from 'types/models/VenuePaymentConfig';
import FactoryWidget from 'types/models/FactoryWidget';
import SNSConfig from 'types/models/SNSConfig';
import VenueDeliveryConfig, { DeliveryTimeRangeConfig } from 'types/models/VenueDeliveryConfig';
import VenueSectionType from 'types/enums/VenueSectionType';
import { parseBanner, parseBanners } from './bannerServices';
import { parseImages } from './imageServices';
import { parseLocalDeliveryAreas } from 'services/localDeliveryAreasServices';
import VenueSection from 'types/models/VenueSection';
import VenueImageType from 'types/enums/VenueImageType';
import Banner from 'types/models/Banner';
import TomorrowAppCrendential from 'types/models/TomorrowAppCredential';
import TomorrowPlan from 'types/models/TomorrowPlan';
import joinURLParams from 'utils/joinUrlParams';
import { formToFormData } from './productServices';
import LuxuryBox from 'types/models/LuxuryBox';
import LuxuryBoxData from 'types/requests/LuxuryBoxData';
import LuxuryBoxMovement from 'types/models/LuxuryBoxMovement';
import { parseOrder } from './orderServices';
import { parseEvents } from './eventServices';

export const getVenues = async (page?: number, searchTerm?: string): Promise<PaginationData<Venue> | null> => {
  try {
    const url = joinURLParams({ page: page ?? 1, searchTerm }, `venues`);
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseVenues(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getVenuesWithStockModuleActive = async (
  page?: number,
  searchTerm?: string,
): Promise<PaginationData<Venue> | null> => {
  try {
    const url = joinURLParams({ page: page ?? 1, searchTerm, stockModule: true }, `venues`);
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseVenues(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getSimpleVenuesWithStockModuleActive = async (
  params?: Record<string, string>,
): Promise<Venue[] | null> => {
  const url = joinURLParams({ ...params, stockModule: true, simple: true }, `venues`);
  const { data } = await axiosInstance({
    method: 'get',
    url,
  });
  return parseVenues(data);
};

export const getSimpleVenues = async (params?: Record<string, string>): Promise<Venue[] | null> => {
  try {
    const url = joinURLParams({ ...params, simple: true }, `venues`);
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });
    return parseVenues(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const bulkSetStockModule = async (venueIds: string[]): Promise<{ updated: number } | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'venues/stock-module/set',
      data: {
        venueIds,
      },
    });
    return {
      updated: data?.updated ?? 0,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getVenuesByCommerce = async (): Promise<Venue[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: '/commerces/get/current-venues',
    });
    return parseVenues(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getApps = async (): Promise<App[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'apps/all',
    });
    return parseApps(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getAllVenues = async (): Promise<Venue[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'venues/get/all',
    });
    return parseVenues(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getVenuesV2 = async (): Promise<Venue[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'apps/BilleteraFAN/venues/get/current-venues/v2',
    });
    return parseVenues(data);
  } catch (error) {
    console.log('getVenuesV2 error', error);
    throw error;
  }
};

export const getVenuesCommercesProducts = async (): Promise<Venue[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'venues/get/all/products',
    });
    return parseVenues(data);
  } catch (error) {
    console.log('getVenuesCommercesProducts error', error);
    throw error;
  }
};

export const getTomorrowPlans = async (): Promise<TomorrowPlan[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: 'venues/tomorrow/plans',
    });
    return parseTomorrowPlans(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeVenue = async (formData: FormData): Promise<Venue | null> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: 'venues',
      data: formData,
    });
    return parseVenue(data);
  } catch (error) {
    console.log('error(storeVenue', error);
    throw error;
  }
};

export const getVenue = async (venueId: string): Promise<Venue | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `venues/${venueId}`,
    });
    return parseVenue(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateVenue = async (venueId: string, formData: FormData): Promise<Venue | null> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `venues/${venueId}`,
      data: formData,
    });
    return parseVenue(data);
  } catch (error) {
    console.log('error(updateVenue)', error);
    throw error;
  }
};

export const deleteVenue = async (venueId: string): Promise<Venue | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'delete',
      url: `venues/${venueId}`,
    });
    return parseVenue(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateVenuePaymentConfig = async (
  venueId: string,
  editVenueConfigForm: VenuePaymentConfig,
): Promise<VenuePaymentConfig | null> => {
  const body = formToFormData(editVenueConfigForm);
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `venues/${venueId}/payment-configs?_method=put`,
      data: body,
    });
    return parseVenuePaymentConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateVenueFactoryWidgets = async (
  venueId: string,
  factoryWidgets: FactoryWidget[],
): Promise<FactoryWidget[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `venues/${venueId}/factory-widgets`,
      data: {
        factoryWidgets,
      },
    });
    return parseVenueFactoryWidgets(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateVenueSNSConfig = async (
  venueId: string,
  snsConfig: SNSConfig,
): Promise<VenuePaymentConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `venues/${venueId}/sns-configs`,
      data: snsConfig,
    });
    return parseVenuePaymentConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateVenueDeliveryConfig = async (
  venueId: string,
  editVenueConfigForm: VenueDeliveryConfig,
): Promise<VenueDeliveryConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `venues/${venueId}/delivery-configs`,
      data: editVenueConfigForm,
    });
    return parseVenueDeliveryConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeTimeRangeConfig = async (
  venueId: string,
  ageRangeConfig: any,
): Promise<DeliveryTimeRangeConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `venues/${venueId}/delivery-configs/time-range-configs`,
      data: ageRangeConfig,
    });
    return parseTimeRangeConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const destroyTimeRangeConfig = async (
  venueId: string,
  ageRangeConfigId: number,
): Promise<DeliveryTimeRangeConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'delete',
      url: `venues/${venueId}/delivery-configs/time-range-configs/${ageRangeConfigId}`,
    });
    return parseTimeRangeConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateTimeRangeConfig = async (
  venueId: string,
  body: any,
  timeRangeConfigId: number,
): Promise<DeliveryTimeRangeConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `venues/${venueId}/delivery-configs/time-range-configs/${timeRangeConfigId}`,
      data: body,
    });
    return parseTimeRangeConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const parseTimeRangeConfig = (data: any): DeliveryTimeRangeConfig => ({
  id: data.id,
  fromHour: data.from_hour,
  toHour: data.to_hour,
});

export const uploadTomorrowBeneficiaries = async (venueId: string, formData: FormData): Promise<boolean> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `venues/${venueId}/upload-tomorrow-beneficiaries`,
      data: formData,
    });
    return data === 'OK';
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const getLuxuryBoxes = async (venueId: string, page?: number): Promise<PaginationData<LuxuryBox> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `venues/${venueId}/luxury-boxes?page=${page ?? 1}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseLuxuryBoxes(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeLuxuryBox = async (venueId: string, luxuryBoxPayload: LuxuryBoxData): Promise<LuxuryBox | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `venues/${venueId}/luxury-boxes`,
      data: luxuryBoxPayload,
    });
    return parseLuxuryBox(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateLuxuryBox = async (
  venueId: string,
  luxuryBoxId: string,
  luxuryBoxPayload: LuxuryBoxData,
): Promise<LuxuryBox | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `luxury-boxes/${venueId}/${luxuryBoxId}`,
      data: luxuryBoxPayload,
    });
    return parseLuxuryBox(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getLuxuryBox = async (luxuryBoxId: string): Promise<LuxuryBox | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `luxury-boxes/${luxuryBoxId}`,
    });
    return parseLuxuryBox(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getLuxuryBoxesByCloverConfig = async (cloverPointConfigId: string): Promise<LuxuryBox[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `luxury-boxes/by-clover-config/${cloverPointConfigId}`,
    });
    return parseLuxuryBoxes(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getLuxuryBoxMovements = async (
  luxuryBoxId: string,
  page?: number,
): Promise<PaginationData<LuxuryBoxMovement> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `luxury-boxes/${luxuryBoxId}/movements?page=${page ?? 1}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseLuxuryBoxMovements(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const payLuxuryBoxMovements = async (luxuryBoxId: string, movements: string[]): Promise<boolean> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `luxury-boxes/${luxuryBoxId}/movements/pay`,
      data: {
        movements,
      },
    });
    return data === 'OK';
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const parseAllVenueSectionTypes = (): any[] => {
  return Object.keys(VenueSectionType)
    .filter((item) => Number(Number(item)) !== VenueSectionType.Banner)
    .map((item) => {
      switch (Number(item)) {
        case VenueSectionType.Promotion:
          return {
            id: Number(item),
            description: 'Promociones',
          };
        case VenueSectionType.Commerce:
          return {
            id: Number(item),
            description: 'Comercios',
          };
        case VenueSectionType.Sparx:
          return {
            id: Number(item),
            description: 'Trivia',
          };
        case VenueSectionType.Ticket:
          return {
            id: Number(item),
            description: 'Ticket',
          };
        case VenueSectionType.Discount:
          return {
            id: Number(item),
            description: 'Descuentos',
          };
        case VenueSectionType.Download:
          return {
            id: Number(item),
            description: 'Descarga',
          };
        case VenueSectionType.Betting:
          return {
            id: Number(item),
            description: 'Apuestas',
          };
        default:
          return;
      }
    })
    .filter((item) => item !== undefined);
};

export const parseVenueSectionType = (type: VenueSectionType, sectionBanner?: Banner): string => {
  switch (Number(type)) {
    case VenueSectionType.Promotion:
      return 'Promociones';
    case VenueSectionType.Commerce:
      return 'Comercios';
    case VenueSectionType.Sparx:
      return 'Trivia';
    case VenueSectionType.Ticket:
      return 'Ticket';
    case VenueSectionType.Discount:
      return 'Descuentos';
    case VenueSectionType.Download:
      return 'Descarga';
    case VenueSectionType.Betting:
      return 'Apuestas';
    case VenueImageType.Banner:
      return sectionBanner?.title ?? 'Banner';
    default:
      return '';
  }
};

export const parseTomorrowPlans = (tomorrowPlans: any): TomorrowPlan[] => {
  return tomorrowPlans.map(
    (tomorrowPlan: any): TomorrowPlan => ({
      id: tomorrowPlan.id,
      description: tomorrowPlan.description,
    }),
  );
};

const parseVenues = (venues: any): Venue[] => {
  return venues.map((venue: any): Venue => parseVenue(venue));
};

export const parseVenue = (venue: any): Venue => {
  return {
    id: venue.id,
    name: venue.name,
    code: venue.code,
    sparxDescription: venue.sparx_description,
    sparxLink: venue.sparx_link,
    ticketDescription: venue.ticket_description,
    ticketLink: venue.ticket_link,
    ticketImageDescription: venue.ticket_image_description,
    enabled: venue.enabled === 1,
    useDifferentIconSet: venue.use_different_icon_set === 1,
    loginIsRequired: Boolean(venue.login_is_required),
    usesStockModule: Boolean(venue.uses_stock_module),
    language: venue.language,
    countryBillingConfigId: venue.country_billing_config_id,
    order: venue.order_priority,
    billingRange: venue.billing_range,
    productItemRedirectToDetails: venue.product_item_redirect_to_details === 1,
    timeZoneIdentifier: venue.local_timezone_identifier,
    usesTomorrowPartnerBenefits: venue.uses_tomorrow_partner_benefits === 1,
    wallioUrl: venue.wallio_url ?? undefined,
    wallioTitle: venue.wallio_title ?? undefined,
    eventName: venue.event_name ?? undefined,
    appColor: venue.app_color ?? undefined,
    appSecondaryColor: venue.app_secondary_color ?? undefined,
    commerces: venue.commerces ? parseCommerces(venue.commerces) : undefined,
    images: venue.images ? parseImages(venue.images) : undefined,
    paymentConfig: venue.payment_config ? parseVenuePaymentConfig(venue.payment_config) : undefined,
    factoryWidgets: venue.factory_widgets ? parseVenueFactoryWidgets(venue.factory_widgets) : undefined,
    bettingTitle: venue.betting_widget_title ?? undefined,
    bettingUrl: venue.betting_widget_url ?? undefined,
    twitterConfig: venue.twitter_config_id ?? undefined,
    deliveryConfig: venue.delivery_config ? parseVenueDeliveryConfig(venue.delivery_config) : undefined,
    apps: venue.apps ? parseApps(venue.apps) : undefined,
    editorId: venue.editor_id ?? undefined,
    entityIdentificator: venue.entity_identificator ?? undefined,
    banners: venue.banners ? parseBanners(venue.banners) : undefined,
    sections: venue.sections ? parseSections(venue.sections) : undefined,
    tomorrowAppCredential: venue.tomorrow_app_credential
      ? parseTomorrowAppCredential(venue.tomorrow_app_credential)
      : undefined,
    tomorrowPartnerMainDocumentType: venue.tomorrow_partner_main_document_type ?? undefined,
    luxuryBoxes: venue.luxury_boxes ? parseLuxuryBoxes(venue.luxury_boxes) : undefined,
    consumptionsLimit: venue.consumptions ?? undefined,
    events: venue.events ? parseEvents(venue.events) : undefined,
  };
};

const parseFiservQrAspect = (data: any) => ({
  fiservQrScreenLetterColor: data.letter_color,
  fiservQrScreenQrBorderColor: data.qr_border_color,
  fiservQrScreenBorderColor: data.screen_border_color,
  fiservQrScreenBackgroundColor: data.screen_background_color,
  fiservQrScreenLogo: { path: data.custom_logo_path, type: '0' },
});

const parseApps = (apps: any): App[] => {
  return apps.map((app: any): App => parseApp(app));
};

const parseApp = (app: any): App => {
  return {
    id: app.id,
    description: app.description,
  };
};

const parseVenuePaymentConfig = (paymentConfig: any): VenuePaymentConfig => {
  return {
    id: paymentConfig.id,
    priorityOverCommerce: paymentConfig.priority_over_commerce,
    cashEnabled: paymentConfig.cash_enabled,
    balanceEnabled: paymentConfig.balance_enabled,
    courtesyEnabled: paymentConfig.courtesy_enabled,
    splitPaymentEnabled: paymentConfig.split_payment_enabled,
    activeProvider: paymentConfig.active_payment_provider_id,
    firstDataConfigId: paymentConfig.first_data_config_id,
    mpConfigId: paymentConfig.mp_config_id,
    mpCheckoutConfigId: paymentConfig.mp_checkout_config_id,
    mpPointConfigId: paymentConfig.mp_point_config_id,
    cloverPointConfigId: paymentConfig.clover_point_config_id,
    izipayConfigId: paymentConfig.izipay_config_id,
    fiservQrConfigId: paymentConfig.fiserv_qr_subsidiary_config_id,
    sitefConfigId: paymentConfig.sitef_config_id,
    fiservQrAspect: paymentConfig.fiserv_qr_aspect ? parseFiservQrAspect(paymentConfig.fiserv_qr_aspect) : null,
  };
};

const parseVenueFactoryWidgets = (factoryWidgets: any): FactoryWidget[] => {
  return factoryWidgets.map(
    (factoryWidgetItem: any): FactoryWidget => ({
      id: factoryWidgetItem.id,
      factoryWidgetLink: factoryWidgetItem.factory_widget,
      height: factoryWidgetItem.height,
    }),
  );
};

const parseVenueDeliveryConfig = (deliveryConfig: any): VenueDeliveryConfig => {
  return {
    id: deliveryConfig.id,
    deliveryEnabled: deliveryConfig.delivery_enabled === 1,
    pickUpEnabled: deliveryConfig.pick_up_enabled === 1,
    isLocalDelivery: deliveryConfig.is_local_delivery === 1,
    priorityOverCommerce: deliveryConfig.priority_over_commerce === 1,
    showPhoneField: deliveryConfig.show_phone_field === 1,
    isPhoneFieldRequired: deliveryConfig.is_phone_field_required === 1,
    pickUpVerificationMethod: deliveryConfig.pick_up_verification_method,
    areas: deliveryConfig.areas ? parseLocalDeliveryAreas(deliveryConfig.areas) : [],
    purchaseToleranceTime: deliveryConfig.purchase_tolerance_time,
    timeRangeConfigs: deliveryConfig.time_range_configs ? parseTimeRangeConfigs(deliveryConfig.time_range_configs) : [],
  };
};

export const parseTimeRangeConfigs = (configs: any): DeliveryTimeRangeConfig[] => {
  return configs.map((config: any) => parseTimeRangeConfig(config));
};

const parseSections = (sections: any): VenueSection<any>[] => {
  return sections.map(
    (section: any): VenueSection<any> => ({
      id: section.id,
      type: section.pivot.venue_section_type_id,
      order: section.order,
      banner: section.banner ? parseBanner(section.banner) : undefined,
    }),
  );
};

const parseTomorrowAppCredential = (tomorrowAppCredential: any): TomorrowAppCrendential => {
  return {
    id: tomorrowAppCredential.id,
    userName: tomorrowAppCredential.username,
    acronym: tomorrowAppCredential.acronym,
  };
};

const parseLuxuryBoxes = (luxuryBoxes: any): LuxuryBox[] => {
  return luxuryBoxes.map((luxuryBox: any): LuxuryBox => parseLuxuryBox(luxuryBox));
};

export const parseLuxuryBox = (luxuryBox: any): LuxuryBox => {
  return {
    id: luxuryBox.pivot !== undefined ? luxuryBox.pivot.luxury_box_id : luxuryBox.id,
    floor: luxuryBox.floor,
    identifierOnFloor: luxuryBox.identifier_on_floor,
    balance: luxuryBox.balance,
    usesCheckingAccount: luxuryBox.uses_checking_account,
    commerces: luxuryBox.commerces ? parseCommerces(luxuryBox.commerces) : undefined,
    createdAt: luxuryBox.created_at,
    updatedAt: luxuryBox.updated_at,
  };
};

const parseLuxuryBoxMovements = (luxuryBoxMovements: any): LuxuryBoxMovement[] => {
  return luxuryBoxMovements.map((luxuryBox: any): LuxuryBoxMovement => parseLuxuryBoxMovement(luxuryBox));
};

const parseLuxuryBoxMovement = (luxuryBoxMovement: any): LuxuryBoxMovement => {
  return {
    id: luxuryBoxMovement.id,
    movementType: luxuryBoxMovement.movement_type,
    amount: luxuryBoxMovement.amount,
    belongsToCheckingAccount: luxuryBoxMovement.belongs_to_checking_account === 1,
    coveredByPayment: luxuryBoxMovement.covered_by_payment === 1,
    order: parseOrder(luxuryBoxMovement.order),
    updatedAt: luxuryBoxMovement.updated_at,
  };
};
